import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import Icon from './AppIcon';

const CharacterNetworkDiagram = ({ nodes, links, onNodeClick, className = '' }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedNode, setSelectedNode] = useState(null);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef?.current) {
        const width = containerRef?.current?.offsetWidth;
        const height = Math.max(600, Math.min(800, width * 0.75));
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!svgRef?.current || !nodes || nodes?.length === 0) return;

    // Clear previous content
    d3?.select(svgRef?.current)?.selectAll('*')?.remove();

    const { width, height } = dimensions;
    const svg = d3?.select(svgRef?.current)?.attr('width', width)?.attr('height', height)?.attr('viewBox', [0, 0, width, height]);

    // Create container group for zoom
    const g = svg?.append('g');

    // Add zoom behavior
    const zoom = d3?.zoom()?.scaleExtent([0.5, 3])?.on('zoom', (event) => {
        g?.attr('transform', event?.transform);
      });

    svg?.call(zoom);

    // Create force simulation
    const simulation = d3?.forceSimulation(nodes)?.force('link', d3?.forceLink(links)?.id(d => d?.id)?.distance(d => 150 - (d?.value * 10)) // Closer for more shared scenes
      )?.force('charge', d3?.forceManyBody()?.strength(-400))?.force('center', d3?.forceCenter(width / 2, height / 2))?.force('collision', d3?.forceCollide()?.radius(50));

    // Create link elements
    const link = g?.append('g')?.selectAll('line')?.data(links)?.join('line')?.attr('stroke', '#94a3b8')?.attr('stroke-opacity', 0.6)?.attr('stroke-width', d => Math.sqrt(d?.value) * 2);

    // Create node groups
    const node = g?.append('g')?.selectAll('g')?.data(nodes)?.join('g')?.attr('cursor', 'pointer')?.call(d3?.drag()?.on('start', dragstarted)?.on('drag', dragged)?.on('end', dragended)
      );

    // Add circles for nodes
    node?.append('circle')?.attr('r', d => d?.isTarget ? 30 : 20)?.attr('fill', d => d?.isTarget ? '#3b82f6' : '#8b5cf6')?.attr('stroke', '#fff')?.attr('stroke-width', 3)?.attr('opacity', 0.9);

    // Add character images (if available)
    node?.filter(d => d?.imageUrl)?.append('image')?.attr('xlink:href', d => d?.imageUrl)?.attr('x', d => d?.isTarget ? -25 : -15)?.attr('y', d => d?.isTarget ? -25 : -15)?.attr('width', d => d?.isTarget ? 50 : 30)?.attr('height', d => d?.isTarget ? 50 : 30)?.attr('clip-path', d => d?.isTarget ? 'circle(25px at center)' : 'circle(15px at center)');

    // Add labels
    node?.append('text')?.text(d => d?.name)?.attr('x', 0)?.attr('y', d => d?.isTarget ? 45 : 35)?.attr('text-anchor', 'middle')?.attr('fill', '#1e293b')?.attr('font-size', d => d?.isTarget ? '14px' : '12px')?.attr('font-weight', d => d?.isTarget ? 'bold' : 'normal')?.attr('pointer-events', 'none');

    // Add line count badges
    node?.append('circle')?.attr('cx', d => d?.isTarget ? 20 : 15)?.attr('cy', d => d?.isTarget ? -20 : -15)?.attr('r', 12)?.attr('fill', '#ef4444')?.attr('stroke', '#fff')?.attr('stroke-width', 2);

    node?.append('text')?.text(d => d?.lineCount > 99 ? '99+' : d?.lineCount)?.attr('x', d => d?.isTarget ? 20 : 15)?.attr('y', d => d?.isTarget ? -16 : -11)?.attr('text-anchor', 'middle')?.attr('fill', '#fff')?.attr('font-size', '10px')?.attr('font-weight', 'bold')?.attr('pointer-events', 'none');

    // Add tooltips
    const tooltip = d3?.select('body')?.append('div')?.attr('class', 'absolute bg-card border border-border rounded-lg shadow-lg p-3 pointer-events-none opacity-0 transition-opacity z-50')?.style('max-width', '200px');

    node?.on('mouseenter', function(event, d) {
      d3?.select(this)?.select('circle')?.attr('opacity', 1)?.attr('stroke-width', 4);
      
      const connections = links?.filter(l => l?.source?.id === d?.id || l?.target?.id === d?.id)?.length;
      
      tooltip?.html(`
          <div class="text-sm">
            <p class="font-bold text-card-foreground mb-1">${d?.name}</p>
            <p class="text-muted-foreground text-xs">Lines: ${d?.lineCount}</p>
            <p class="text-muted-foreground text-xs">Connections: ${connections}</p>
          </div>
        `)?.style('left', (event?.pageX + 10) + 'px')?.style('top', (event?.pageY - 10) + 'px')?.style('opacity', 1);
    })?.on('mouseleave', function(event, d) {
      d3?.select(this)?.select('circle')?.attr('opacity', 0.9)?.attr('stroke-width', 3);
      tooltip?.style('opacity', 0);
    })?.on('click', function(event, d) {
      event?.stopPropagation();
      setSelectedNode(d);
      if (onNodeClick) {
        onNodeClick(d);
      }
    });

    // Update positions on simulation tick
    simulation?.on('tick', () => {
      link?.attr('x1', d => d?.source?.x)?.attr('y1', d => d?.source?.y)?.attr('x2', d => d?.target?.x)?.attr('y2', d => d?.target?.y);

      node?.attr('transform', d => `translate(${d?.x},${d?.y})`);
    });

    // Drag functions
    function dragstarted(event) {
      if (!event?.active) simulation?.alphaTarget(0.3)?.restart();
      event?.subject && (event.subject.fx = event?.subject?.x);
      event?.subject && (event.subject.fy = event?.subject?.y);
    }

    function dragged(event) {
      event?.subject && (event.subject.fx = event?.x);
      event?.subject && (event.subject.fy = event?.y);
    }

    function dragended(event) {
      if (!event?.active) simulation?.alphaTarget(0);
      event?.subject && (event.subject.fx = null);
      event?.subject && (event.subject.fy = null);
    }

    // Cleanup
    return () => {
      simulation?.stop();
      tooltip?.remove();
    };
  }, [nodes, links, dimensions, onNodeClick]);

  if (!nodes || nodes?.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted rounded-lg">
        <div className="text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No character relationships found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={containerRef} className="w-full">
        <svg ref={svgRef} className="border border-border rounded-lg bg-background" />
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary border-2 border-white" />
          <span className="text-muted-foreground">Current Character</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-purple-500 border-2 border-white" />
          <span className="text-muted-foreground">Related Characters</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-slate-400" />
          <span className="text-muted-foreground">Shared Scenes (thicker = more)</span>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground">Drag nodes • Scroll to zoom • Click for details</span>
        </div>
      </div>

      {/* Selected Node Info */}
      {selectedNode && (
        <div className="mt-4 p-4 bg-card border border-border rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-card-foreground mb-1">{selectedNode?.name}</h4>
              <p className="text-sm text-muted-foreground">Lines in play: {selectedNode?.lineCount}</p>
              <p className="text-sm text-muted-foreground">
                Connections: {links?.filter(l => l?.source?.id === selectedNode?.id || l?.target?.id === selectedNode?.id)?.length}
              </p>
            </div>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterNetworkDiagram;